import { Helmet } from "react-helmet-async";

interface FAQItem {
  question: string;
  answer: string;
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface StructuredDataProps {
  type?: 'FAQPage' | 'Article' | 'HowTo' | 'Product' | 'Review' | 'BreadcrumbList';
  faqs?: FAQItem[];
  breadcrumbs?: BreadcrumbItem[];
  article?: {
    headline: string;
    datePublished: string;
    dateModified: string;
    author: string;
    description: string;
  };
  howTo?: {
    name: string;
    description: string;
    steps: Array<{ name: string; text: string }>;
  };
  review?: {
    itemName: string;
    ratingValue: number;
    reviewCount: number;
    bestRating?: number;
  };
}

export const StructuredData = ({ type, faqs, breadcrumbs, article, howTo, review }: StructuredDataProps) => {
  let schema = {};

  if (type === 'BreadcrumbList' && breadcrumbs) {
    schema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": crumb.url
      }))
    };
  }

  if (type === 'FAQPage' && faqs) {
    schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };
  }

  if (type === 'Article' && article) {
    schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.headline,
      "datePublished": article.datePublished,
      "dateModified": article.dateModified,
      "author": {
        "@type": "Organization",
        "name": article.author
      },
      "description": article.description,
      "publisher": {
        "@type": "Organization",
        "name": "RandomGen",
        "logo": {
          "@type": "ImageObject",
          "url": "https://numbers-generator.com/icon-512.png"
        }
      }
    };
  }

  if (type === 'HowTo' && howTo) {
    schema = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": howTo.name,
      "description": howTo.description,
      "step": howTo.steps.map((step, index) => ({
        "@type": "HowToStep",
        "position": index + 1,
        "name": step.name,
        "text": step.text
      }))
    };
  }

  if (type === 'Review' && review) {
    schema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": review.itemName,
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": review.ratingValue,
        "reviewCount": review.reviewCount,
        "bestRating": review.bestRating || 5
      }
    };
  }

  if (Object.keys(schema).length === 0) return null;

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};